import JSZip from 'jszip';
import BaseConverter from './BaseConverter.js';
import cbzCss from '../../styles/cbz-conversion.css?raw';

/**
 * Converter for comic book files (CBZ, CBR, CB7) to EPUB format
 */
class ComicConverter extends BaseConverter {
  /**
   * Check if a file is a CBZ file
   */
  isCbzFile(fileName) {
    return fileName.toLowerCase().endsWith('.cbz');
  }

  /**
   * Check if a file is a CBR file
   */
  isCbrFile(fileName) {
    return fileName.toLowerCase().endsWith('.cbr');
  }

  /**
   * Check if a file is a CB7 file
   */
  isCb7File(fileName) {
    return fileName.toLowerCase().endsWith('.cb7');
  }

  /**
   * Check if a file is a ZIP file
   */
  isZipFile(fileName) {
    return fileName.toLowerCase().endsWith('.zip');
  }

  /**
   * Check if a file is any supported comic book format
   */
  isComicFile(fileName) {
    return this.isCbzFile(fileName) || this.isCbrFile(fileName) || this.isCb7File(fileName) || this.isZipFile(fileName);
  }

  /**
   * Convert comic book file to EPUB
   */
  async convertToEpub(comicFile, options, onFileProgress) {
    console.log('Converting comic file:', comicFile.name);
    
    let archive;
    
    // Check for unsupported CBR files
    if (this.isCbrFile(comicFile.name)) {
      throw new Error('CBR files are not currently supported. Please convert your CBR file to CBZ format first using a tool like 7-Zip or WinRAR.');
    }
    
    // Determine file type and extract accordingly
    if (this.isCb7File(comicFile.name)) {
      archive = await this.extractCb7File(comicFile);
    } else {
      console.log('Treating as CBZ/ZIP file');
      archive = await JSZip.loadAsync(comicFile);
    }
    
    console.log('Processing as comic book file');
    const epubZip = new JSZip();

    // Create META-INF directory and container.xml
    this.createContainerXml(epubZip);
    
    // Create mimetype file
    this.createMimetypeFile(epubZip);
    
    // Create OEBPS directory structure
    await this.createOebpsStructure(epubZip, comicFile.name, archive, options, onFileProgress);

    // Generate EPUB as blob
    return await this.generateEpubBlob(epubZip, options.compressionLevel);
  }

  /**
   * Extract CBR (RAR) file - Currently not supported
   * This method is kept for future implementation when a suitable browser-based RAR library becomes available
   */
  async extractCbrFile(file) {
    throw new Error('CBR file extraction is not currently supported. Please convert your CBR file to CBZ format first.');
  }

  /**
   * Extract CB7 (7-Zip) file
   */
  async extractCb7File(file) {
    const arrayBuffer = await file.arrayBuffer();
    
    // Create a JSZip-like interface for CB7 files
    const cb7Archive = {
      files: {},
      forEach: function(callback) {
        for (const [path, fileData] of Object.entries(this.files)) {
          callback(path, fileData);
        }
      },
      file: function(path) {
        const fileData = this.files[path];
        if (!fileData) return null;
        
        return {
          async: function(type) {
            if (type === 'arraybuffer') {
              return fileData.buffer;
            }
            return fileData.buffer;
          }
        };
      }
    };

    // For browser compatibility, we'll treat CB7 files as ZIP files as a fallback
    // This is because proper 7-Zip extraction requires Node.js or WebAssembly
    try {
      console.log('CB7 support: Attempting to extract as ZIP (fallback method)');
      
      // Try to extract as ZIP first (some CB7 files might be ZIP-compatible)
      const zip = await JSZip.loadAsync(arrayBuffer);
      
      // Convert JSZip to our archive format
      zip.forEach((relativePath, file) => {
        if (!file.dir && this.isImageFile(relativePath)) {
          cb7Archive.files[relativePath] = {
            buffer: null, // Will be loaded when needed
            name: relativePath,
            zipFile: file
          };
        }
      });
      
      // Override the file method to handle JSZip extraction
      cb7Archive.file = function(path) {
        const fileData = this.files[path];
        if (!fileData || !fileData.zipFile) return null;
        
        return {
          async: async function(type) {
            if (type === 'arraybuffer') {
              return await fileData.zipFile.async('arraybuffer');
            }
            return await fileData.zipFile.async('arraybuffer');
          }
        };
      };
      
      console.log(`CB7 extraction successful: Found ${Object.keys(cb7Archive.files).length} image files`);
      
    } catch (error) {
      console.warn('CB7 extraction failed, creating empty archive:', error.message);
      // Return empty archive if extraction fails
    }

    return cb7Archive;
  }

  /**
   * Create OEBPS directory structure for comic book conversion
   */
  async createOebpsStructure(epubZip, bookTitle, cbzZip, options, onFileProgress) {
    const cleanTitle = bookTitle.replace('.cbz', '').replace('.cbr', '').replace('.cb7', '');
    
    // Create content.opf
    await this.createContentOpf(epubZip, cleanTitle, cbzZip, options);
    
    // Create toc.ncx
    this.createTocNcx(epubZip, cleanTitle);
    
    // Process comic images and create HTML pages
    await this.processComicImages(epubZip, cbzZip, options, onFileProgress);
  }

  /**
   * Create content.opf file for comic book EPUB
   */
  async createContentOpf(epubZip, bookTitle, cbzZip, options) {
    let contentOpf = `<?xml version="1.0" encoding="UTF-8"?>
<package xmlns="http://www.idpf.org/2007/opf" unique-identifier="BookId" version="2.0">
    <metadata xmlns:dc="http://purl.org/dc/elements/1.1/" xmlns:opf="http://www.idpf.org/2007/opf">
        <dc:title>${bookTitle}</dc:title>
        <dc:creator>Converted from CBZ</dc:creator>
        <dc:language>en</dc:language>
        <dc:identifier id="BookId">urn:uuid:${this.generateUUID()}</dc:identifier>
    </metadata>
    <manifest>
        <item id="ncx" href="toc.ncx" media-type="application/x-dtbncx+xml"/>
        <item id="css" href="style.css" media-type="text/css"/>`;

    // Add image and HTML items
    const imageFiles = this.getImageFiles(cbzZip);
    for (let i = 0; i < imageFiles.length; i++) {
      const pageNumber = i + 1;
      const fileName = imageFiles[i];
      contentOpf += `
        <item id="page_${pageNumber}" href="page_${pageNumber}.html" media-type="application/xhtml+xml"/>
        <item id="image_${pageNumber}" href="images/${fileName}" media-type="${this.getImageMimeType(fileName)}"/>`;
    }

    contentOpf += `
    </manifest>
    <spine toc="ncx">`;

    // Add spine items
    for (let i = 0; i < imageFiles.length; i++) {
      const pageNumber = i + 1;
      contentOpf += `
        <itemref idref="page_${pageNumber}"/>`;
    }

    contentOpf += `
    </spine>
</package>`;

    epubZip.file('OEBPS/content.opf', contentOpf);
  }

  /**
   * Create toc.ncx file for comic book EPUB
   */
  createTocNcx(epubZip, bookTitle) {
    const tocNcx = `<?xml version="1.0" encoding="UTF-8"?>
<ncx xmlns="http://www.daisy.org/z3986/2005/ncx/" version="2005-1">
    <head>
        <meta name="dtb:uid" content="urn:uuid:${this.generateUUID()}"/>
        <meta name="dtb:depth" content="1"/>
        <meta name="dtb:totalPageCount" content="0"/>
        <meta name="dtb:maxPageNumber" content="0"/>
    </head>
    <docTitle>
        <text>${bookTitle}</text>
    </docTitle>
    <navMap>
        <navPoint id="navpoint-1" playOrder="1">
            <navLabel><text>Page 1</text></navLabel>
            <content src="page_1.html"/>
        </navPoint>
    </navMap>
</ncx>`;

    epubZip.file('OEBPS/toc.ncx', tocNcx);
  }

  /**
   * Process comic book images and create HTML pages
   */
  async processComicImages(epubZip, cbzZip, options, onFileProgress) {
    // Create images directory
    epubZip.folder('OEBPS/images');
    
    // Create CSS file
    this.createCssFile(epubZip);
    
    // Process images
    const imageFiles = this.getImageFiles(cbzZip);
    
    for (let i = 0; i < imageFiles.length; i++) {
      const fileName = imageFiles[i];
      const pageNumber = i + 1;
      
      try {
        // Get original image data
        const originalImageData = await cbzZip.file(fileName).async('arraybuffer');
        
        // Process and optimize image
        const processedImageData = await this.processImage(
          originalImageData, 
          fileName, 
          options
        );
        
        // Add image to EPUB
        epubZip.file(`OEBPS/images/${fileName}`, processedImageData);
        
        // Create HTML page
        this.createHtmlPage(epubZip, fileName, pageNumber);
        
        // Report progress for this image
        if (onFileProgress) {
          const progress = ((i + 1) / imageFiles.length) * 100;
          onFileProgress(progress);
        }
      } catch (error) {
        console.error(`Error processing image ${fileName}:`, error);
        throw error;
      }
    }
  }

  /**
   * Create CSS file for comic book EPUB
   */
  createCssFile(epubZip) {
    epubZip.file('OEBPS/style.css', cbzCss);
  }

  /**
   * Create HTML page for a single comic book page
   */
  createHtmlPage(epubZip, imageName, pageNumber) {
    const htmlContent = `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE html PUBLIC "-//W3C//DTD XHTML 1.1//EN" "http://www.w3.org/TR/xhtml11/DTD/xhtml11.dtd">
<html xmlns="http://www.w3.org/1999/xhtml">
<head>
    <title>Page ${pageNumber}</title>
    <link rel="stylesheet" type="text/css" href="style.css"/>
</head>
<body>
    <img src="images/${imageName}" alt="Page ${pageNumber}" />
</body>
</html>`;

    epubZip.file(`OEBPS/page_${pageNumber}.html`, htmlContent);
  }

  /**
   * Process and optimize image if needed
   */
  async processImage(imageData, fileName, options) {
    if (!options.optimizeImages) {
      return imageData;
    }

    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        // Calculate new dimensions
        const { width, height } = this.calculateNewSize(
          img.width, 
          img.height, 
          options.maxWidth, 
          options.maxHeight
        );
        
        // Set canvas size
        canvas.width = width;
        canvas.height = height;
        
        // Draw resized image
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to blob with quality
        canvas.toBlob((blob) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.readAsArrayBuffer(blob);
        }, this.getImageMimeType(fileName), options.imageQuality);
      };
      
      const blob = new Blob([imageData], { type: this.getImageMimeType(fileName) });
      img.src = URL.createObjectURL(blob);
    });
  }

  /**
   * Get list of image files from comic book archive
   */
  getImageFiles(cbzZip) {
    const files = [];
    cbzZip.forEach((relativePath, file) => {
      if (!file.dir && this.isImageFile(relativePath)) {
        files.push(relativePath);
      }
    });
    return files.sort();
  }
}

export default ComicConverter;
